// src/Hooks/useNotifications.js
import { useEffect, useRef, useState } from "react";

/**
 * useNotifications
 * - Establishes a WebSocket connection to "/ws/notifications" on the current host
 * - Provides automatic reconnection with exponential backoff
 * - Sends a lightweight heartbeat to keep the connection alive
 * - Invokes `onEvent` whenever a message is received (so the app can refetch state safely)
 */
export default function useNotifications({ onEvent } = {}) {
  const [status, setStatus] = useState("connecting"); // connecting | connected | reconnecting | offline
  const wsRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const heartbeatRef = useRef(null);
  const attemptsRef = useRef(0);
  const unmountedRef = useRef(false);

  useEffect(() => {
    // Build WS URL from REACT_APP_API_BASE if provided; else use current origin
    let origin;
    try {
      origin = process.env.REACT_APP_API_BASE
        ? new URL(process.env.REACT_APP_API_BASE).origin
        : window.location.origin;
    } catch (e) {
      origin = window.location.origin;
    }
    const wsUrl = origin.replace(/^http(s?):/, "ws$1:") + "/ws/notifications";

    function clearTimers() {
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      if (heartbeatRef.current) {
        clearInterval(heartbeatRef.current);
        heartbeatRef.current = null;
      }
    }

    function startHeartbeat() {
      // Send a ping every 30s; server may respond with pong (optional)
      heartbeatRef.current = setInterval(() => {
        try {
          if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
            wsRef.current.send("ping");
          }
        } catch (e) {
          // no-op
        }
      }, 30000);
    }

    function connect() {
      try {
        wsRef.current = new WebSocket(wsUrl);
      } catch (e) {
        scheduleReconnect();
        return;
      }

      wsRef.current.onopen = () => {
        setStatus("connected");
        attemptsRef.current = 0;
        startHeartbeat();
      };

      wsRef.current.onmessage = (event) => {
        // Ignore heartbeats
        if (event?.data === "pong") return;
        // Defer to outside handler to decide how to update state
        if (typeof onEvent === "function") {
          onEvent(event);
        }
      };

      wsRef.current.onerror = () => {
        // Let onclose handle reconnect path
      };

      wsRef.current.onclose = () => {
        clearTimers();
        if (unmountedRef.current) return;
        scheduleReconnect();
      };
    }

    function scheduleReconnect() {
      setStatus("reconnecting");
      const attempt = attemptsRef.current++;
      const base = Math.min(30_000, 1000 * Math.pow(2, attempt));
      const jitter = Math.floor(Math.random() * 1000);
      const delay = base + jitter;
      reconnectTimerRef.current = setTimeout(() => {
        if (!unmountedRef.current) connect();
      }, delay);
    }

    connect();

    return () => {
      unmountedRef.current = true;
      clearTimers();
      try {
        if (wsRef.current) wsRef.current.close();
      } catch (e) {
        // no-op
      }
    };
  }, [onEvent]);

  return { status };
}
